import React, { memo, useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';
import RoleGroupTooltip from '../../pages/map-level/components/RoleGroupTooltip';
import { getLocalDropdownPosition } from '../../components/getDropdownPosition';
import { stripHtml } from '../../components/StripHtml';
import { cleanHyphens } from '../../utils/hyphenateText';

const SwimlaneRightsideBox = ({ data, processDefaultlanguage_id, langMap, editable, dragging }) => {
  const [title, setTitle] = useState(() =>
    cleanHyphens(stripHtml(data.details?.title || data.label || ''))
  );
  const contentEditableRef = useRef(null);
  const [autoFocus, setAutoFocus] = useState(data.autoFocus);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [topPos, setTopPos] = useState('50%');

  const isRoleGroup = !!data.isRoleGroup; // NEW: single source of truth

  // Sync label
  useEffect(() => {
    const isFocused =
      contentEditableRef.current && document.activeElement === contentEditableRef.current;
    if (!isFocused) {
      const incoming = data.details?.title !== undefined ? data.details?.title : data.label;
      if (incoming !== undefined) {
        setTitle(cleanHyphens(stripHtml(incoming)));
      }
    }
  }, [data.details?.title, data.label]);

  // Handle auto focus in edit mode
  useEffect(() => {
    if (editable && autoFocus && contentEditableRef.current) {
      setTimeout(() => {
        const el = contentEditableRef.current;
        el.focus({ preventScroll: true });

        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }

        setAutoFocus(false);
      }, 0);
    }
  }, [autoFocus, editable]);

  // Dynamic top position calculation in edit mode — only relevant for role groups
  useEffect(() => {
    if (isRoleGroup && editable && isHovered && wrapperRef.current && tooltipRef.current) {
      requestAnimationFrame(() => {
        if (!wrapperRef.current || !tooltipRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        const clickX = rect.right;
        const clickY = rect.top + rect.height / 2;

        const { y } = getLocalDropdownPosition(
          clickX,
          clickY,
          tooltipRect.width,
          tooltipRect.height
        );

        const container = document.querySelector('.publishcontainer');
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const localTop = y - (rect.top - containerRect.top);

        setTopPos(`${localTop}px`);
      });
    }
  }, [isHovered, editable, isRoleGroup]);

  // Z-Index parent adjustment on hover — only relevant for role groups
  useEffect(() => {
    if (!isRoleGroup) return; // NEW: skip entirely for normal boxes
    const parentNode = wrapperRef.current?.closest('.react-flow__node');
    if (parentNode) {
      if (isHovered) {
        parentNode.style.setProperty('z-index', '100000000', 'important');
      } else {
        parentNode.style.removeProperty('z-index');
      }
    }
    return () => {
      if (parentNode) {
        parentNode.style.removeProperty('z-index');
      }
    };
  }, [isHovered, isRoleGroup]);

  // Track dragging so hover never opens the tooltip mid-move
  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  useEffect(() => {
    if (dragging) {
      setIsHovered(false);
    }
  }, [dragging]);

  const handleMouseDown = () => {
    if (isRoleGroup) {
      setIsDragging(true);
      setIsHovered(false);
    }
  };

  const handleChange = (e) => {
    const rawVal = e.target.value;
    setTitle(rawVal);
    const cleanVal = cleanHyphens(rawVal);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleBlur = () => {
    const rawVal = contentEditableRef.current
      ? contentEditableRef.current.innerText || contentEditableRef.current.textContent || title
      : title;
    const cleanVal = cleanHyphens(rawVal);
    setTitle(cleanVal);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleBoxClick = (e) => {
    if (editable) {
      if (e.target.classList.contains('nodrag')) {
        return;
      }
      if (contentEditableRef.current && document.activeElement !== contentEditableRef.current) {
        contentEditableRef.current.focus();

        const el = contentEditableRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFocus = (e) => {
    e.stopPropagation();
  };

  // NEW: hover only ever activates for role-group boxes, and never while dragging
  const handleMouseEnter = () => {
    if (isRoleGroup && !isDragging && !dragging) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isRoleGroup) {
      setIsHovered(false);
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={styles.wrapper}
      onClick={handleBoxClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          ...styles.box,
          backgroundColor: 'red',
          filter: isRoleGroup ? 'drop-shadow(0px 0px 10px #0000004f)' : 'none',
        }}
      >
        {editable ? (
          <ContentEditable
            innerRef={contentEditableRef}
            html={title}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => handleChange({ target: { value: e.target.value } })}
            onKeyDown={handleKeyDown}
            placeholder=""
            style={styles.contentEditable}
            className="nodrag"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div style={styles.label}>
            {cleanHyphens(stripHtml(data.details?.title || data.label || title || ''))}
          </div>
        )}
      </div>
      {isRoleGroup &&
        (isHovered || data.forceShowTooltip) &&
        !isDragging &&
        !dragging &&
        data.roles && (
          <div
            ref={tooltipRef}
            onMouseEnter={() => !isDragging && !dragging && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              position: 'absolute',
              left: '100%',
              top: editable ? topPos : '50%',
              transform: editable ? 'none' : 'translateY(-50%)',
              zIndex: 1000000,
            }}
          >
            <RoleGroupTooltip
              roles={data.roles}
              groupName={title}
              langMap={langMap}
              processDefaultlanguage_id={processDefaultlanguage_id}
            />
          </div>
        )}
    </div>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '80%',
    height: '72%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  box: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color: '#002060',
    width: '100%',
    height: '100%',
    padding: '0px',
    boxSizing: 'border-box',
    overflow: 'visible',
  },
  contentEditable: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: '12px',
    lineHeight: '1.1',
    width: '100%',
    outline: 'none',
    textAlign: 'center',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontFamily: "'Poppins', sans-serif",
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    cursor: 'text',
    hyphens: 'none',
    WebkitHyphens: 'none',
    msHyphens: 'none',
  },
  label: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: '12px',
    fontFamily: "'Poppins', sans-serif",
    lineHeight: '1.1',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center',
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    hyphens: 'none',
    WebkitHyphens: 'none',
    msHyphens: 'none',
  },
};

export default memo(SwimlaneRightsideBox);
